/**
 * Carteira account users — MobileService `/v1/ib/account-users`.
 * Same UX pattern as Baas `/baas/usuarios`, with Carteira permission catalog.
 * Create provisions AccountPerson when needed and activates wallet membership
 * immediately (no OTP verify step in the invite wizard).
 * Existing Valut login → wallet-access grant e-mail; new person → primeiro acesso.
 */

import api from "./api";
import { isIbProductEnabled } from "../Utils/ibProductFeatures";

/**
 * Carteira roles (AccountWalletUsers.Role).
 * Operador is NOT a product/menu — it means "can transact on current products,
 * cannot manage Usuários". Administrador = full. Visualizador = read-only.
 */
export const CARTEIRA_USER_ROLES = ["admin", "operator", "viewer"];

export const CARTEIRA_USER_STATUSES = ["active", "suspended", "pending"];

/**
 * Invite Permissões groups = current IB sidebar menus + write actions.
 * Dropped: antecipação, pagadoria, payroll, pontos de venda (no menus today).
 * `requiredFeature` hides the group when the instance product is off.
 */
export const CARTEIRA_PERMISSION_GROUPS = [
  {
    id: "extract",
    group: "Extrato",
    groupKey: "walletUsers.permissionGroups.extract",
    permissions: [
      { id: "statementsview", name: "Visualizar início e extrato", access: "view" },
      { id: "statementsbalanceview", name: "Visualizar saldos", access: "view" },
    ],
  },
  {
    id: "receipts",
    group: "Comprovantes",
    groupKey: "walletUsers.permissionGroups.receipts",
    permissions: [
      { id: "receiptsview", name: "Visualizar comprovantes", access: "view" },
    ],
  },
  {
    id: "refunds",
    group: "Estornos",
    groupKey: "walletUsers.permissionGroups.refunds",
    permissions: [
      { id: "refundsview", name: "Visualizar estornos", access: "view" },
    ],
  },
  {
    id: "stables",
    group: "Stables",
    groupKey: "walletUsers.permissionGroups.stables",
    requiredFeature: "stables",
    permissions: [
      { id: "stablesview", name: "Visualizar Stables", access: "view" },
      { id: "stablesoperate", name: "Comprar, vender e movimentar Stables", access: "write" },
    ],
  },
  {
    id: "international",
    group: "Conta internacional",
    groupKey: "walletUsers.permissionGroups.international",
    requiredFeature: "fiat",
    permissions: [
      { id: "internationalview", name: "Visualizar conta internacional", access: "view" },
      { id: "internationaltransfer", name: "Transferir / movimentar", access: "write" },
    ],
  },
  {
    id: "virtualAccounts",
    group: "Contas virtuais",
    groupKey: "walletUsers.permissionGroups.virtualAccounts",
    hideWhenExitRailVirtual: true,
    permissions: [
      { id: "virtualaccountsview", name: "Visualizar contas virtuais", access: "view" },
      { id: "virtualaccountscreate", name: "Criar conta virtual", access: "write" },
    ],
  },
  {
    id: "pix",
    group: "Pix",
    groupKey: "walletUsers.permissionGroups.pix",
    requiredFeature: "pix",
    permissions: [
      { id: "pixview", name: "Visualizar Pix / chaves", access: "view" },
      { id: "paypix", name: "Pagar / transferir Pix", access: "write" },
      { id: "receivepix", name: "Receber Pix (QR / vendas)", access: "write" },
      { id: "pixkeycreate", name: "Cadastrar chave Pix", access: "write" },
      { id: "pixkeydelete", name: "Excluir chave Pix", access: "write" },
    ],
  },
  {
    id: "boletos",
    group: "Boletos",
    groupKey: "walletUsers.permissionGroups.boletos",
    permissions: [
      { id: "boletocreateview", name: "Visualizar boletos", access: "view" },
      { id: "payboleto", name: "Pagar boleto", access: "write" },
      { id: "createboleto", name: "Criar boleto", access: "write" },
    ],
  },
  {
    id: "transfers",
    group: "Transferências",
    groupKey: "walletUsers.permissionGroups.transfers",
    permissions: [
      { id: "p2pview", name: "Visualizar transferências P2P", access: "view" },
      { id: "transp2p", name: "Transferir P2P", access: "write" },
    ],
  },
  {
    id: "cards",
    group: "Cartões",
    groupKey: "walletUsers.permissionGroups.cards",
    requiredFeature: "cards",
    permissions: [
      { id: "mycardview", name: "Visualizar cartões", access: "view" },
    ],
  },
  {
    id: "users",
    group: "Usuários",
    groupKey: "walletUsers.permissionGroups.users",
    permissions: [
      { id: "operatorsview", name: "Visualizar usuários", access: "view" },
      { id: "operatorscreate", name: "Gerenciar usuários", access: "write" },
    ],
  },
];

export const CARTEIRA_ROLE_PERMISSIONS = {
  admin: ["admin"],
  operator: [
    "statementsview",
    "statementsbalanceview",
    "receiptsview",
    "refundsview",
    "stablesview",
    "stablesoperate",
    "internationalview",
    "internationaltransfer",
    "virtualaccountsview",
    "virtualaccountscreate",
    "pixview",
    "paypix",
    "receivepix",
    "pixkeycreate",
    "pixkeydelete",
    "boletocreateview",
    "payboleto",
    "createboleto",
    "p2pview",
    "transp2p",
    "operatorsview",
    "mycardview",
  ],
  viewer: [
    "statementsview",
    "statementsbalanceview",
    "receiptsview",
    "refundsview",
    "stablesview",
    "internationalview",
    "virtualaccountsview",
    "pixview",
    "boletocreateview",
    "p2pview",
    "mycardview",
  ],
};

/**
 * Groups shown in the invite Permissões step — hide products the instance
 * does not have (and Contas virtuais on exit-rail VIRTUAL accounts).
 */
export const getVisibleCarteiraPermissionGroups = ({
  features,
  isExitRailVirtual = false,
  isFeatureEnabled,
} = {}) =>
  CARTEIRA_PERMISSION_GROUPS.filter((group) => {
    if (group.hideWhenExitRailVirtual && isExitRailVirtual) return false;
    if (!group.requiredFeature) return true;
    if (typeof isFeatureEnabled === "function") {
      return isFeatureEnabled(group.requiredFeature);
    }
    return isIbProductEnabled(features, group.requiredFeature);
  });

/** Write codes a viewer / visualizador must never receive. */
export const CARTEIRA_WRITE_PERMISSION_IDS = CARTEIRA_PERMISSION_GROUPS.flatMap((group) =>
  group.permissions.filter((permission) => permission.access === "write").map((permission) => permission.id),
);

/**
 * True when the session has no write tokens (role viewer / visualizador).
 * Hide create / transfer / invite CTAs when this is true.
 */
export const isCarteiraReadOnly = (permissions) => {
  const list = Array.isArray(permissions) ? permissions.map(String) : [];
  if (list.some((permission) => permission.toLowerCase() === "admin")) return false;
  return !list.some((permission) => CARTEIRA_WRITE_PERMISSION_IDS.includes(permission));
};

const mapUser = (row) => {
  const statusRaw = String(row?.status ?? "active").trim().toLowerCase();
  const status =
    statusRaw === "active" || statusRaw === "suspended" || statusRaw === "pending"
      ? statusRaw
      : "suspended";
  const roleRaw = String(row?.role ?? "admin").trim().toLowerCase();
  const role =
    roleRaw === "admin" || roleRaw === "operator" || roleRaw === "viewer" ? roleRaw : "admin";
  const permissions = Array.isArray(row?.permissions)
    ? row.permissions.map(String)
    : CARTEIRA_ROLE_PERMISSIONS[role] ?? [];

  return {
    id: String(row?.id ?? ""),
    personId: Number(row?.person_id ?? row?.personId) || null,
    name: String(row?.name ?? "").trim() || `Person ${row?.person_id ?? ""}`,
    email: String(row?.email ?? "").trim(),
    nationalRegistration: String(
      row?.national_registration ?? row?.nationalRegistration ?? "",
    ).trim(),
    accountId: row?.account_id ?? row?.accountId ?? null,
    role,
    permissions,
    status,
    invitePending: Boolean(row?.invite_pending ?? row?.invitePending ?? status === "pending"),
    lastAccess: row?.last_access ?? row?.lastAccess ?? null,
    createdAt: row?.created_at ?? row?.createdAt ?? null,
  };
};

const mapVerification = (row) => {
  if (!row) return null;
  return {
    required: Boolean(row.required ?? true),
    challengeId: row.challenge_id ?? row.challengeId ?? null,
    channel: String(row.channel ?? "email"),
    purpose: String(row.purpose ?? "wallet_invite"),
    expiresAt: row.expires_at ?? row.expiresAt ?? null,
    code: row.code ?? null,
  };
};

/**
 * Prefer API detail, but never surface CreateAccountWalletUsers / InvitePending SQL banners.
 * Those scripts are optional — invite works via AccountPersonAccounts.
 */
export const carteiraUsersErrorMessage = (error, fallback) => {
  const detail = String(
    error?.response?.data?.detail
      || error?.detail
      || error?.response?.data?.description
      || error?.data?.description
      || error?.data?.detail
      || error?.message
      || "",
  ).trim();
  if (
    /account_id is not an IB account/i.test(detail)
    || /account_not_allowed/i.test(String(error?.code || error?.data?.error || ""))
  ) {
    return fallback;
  }
  if (
    /CreateAccountWalletUsers|rbac_not_migrated|InvitePending\s+alter|before managing/i.test(
      detail,
    )
  ) {
    return fallback;
  }
  return detail || fallback;
};

export const fetchCarteiraUsers = async () => {
  const { data } = await api.get("/v1/ib/account-users");
  const items = Array.isArray(data?.items) ? data.items.map(mapUser) : [];
  return {
    items,
    manageSupported: Boolean(data?.manage_supported ?? data?.manageSupported),
    availablePermissions: Array.isArray(data?.available_permissions)
      ? data.available_permissions.map(String)
      : Array.isArray(data?.availablePermissions)
        ? data.availablePermissions.map(String)
        : CARTEIRA_PERMISSION_GROUPS.flatMap((g) => g.permissions.map((p) => p.id)),
  };
};

/**
 * Lookup AccountPerson by CPF/CNPJ before creating a wallet invite.
 * @returns {{ found: boolean, verified: boolean, alreadyMember: boolean, hasPlatformAccess: boolean, accessStatus: string, userId: number|null, nationalRegistration: string, person: object|null }}
 */
export const lookupCarteiraPersonByDocument = async (nationalRegistration) => {
  const digits = String(nationalRegistration ?? "").replace(/\D/g, "");
  const { data } = await api.get("/v1/ib/account-users/lookup", {
    params: { national_registration: digits },
  });

  const person = data?.person;
  const userIdRaw = Number(data?.user_id ?? data?.userId);
  return {
    found: Boolean(data?.found),
    verified: Boolean(data?.verified),
    alreadyMember: Boolean(data?.already_member ?? data?.alreadyMember),
    hasPlatformAccess: Boolean(data?.has_platform_access ?? data?.hasPlatformAccess),
    accessStatus: String(data?.access_status ?? data?.accessStatus ?? "pending"),
    userId: Number.isFinite(userIdRaw) && userIdRaw > 0 ? userIdRaw : null,
    nationalRegistration: String(
      data?.national_registration ?? data?.nationalRegistration ?? digits,
    ),
    person: person
      ? {
          personId: Number(person.person_id ?? person.personId) || null,
          name: String(person.name ?? "").trim(),
          middleName: String(person.middle_name ?? person.middleName ?? "").trim(),
          lastName: String(person.last_name ?? person.lastName ?? "").trim(),
          email: String(person.email ?? "").trim(),
          nationalRegistration: String(
            person.national_registration ?? person.nationalRegistration ?? digits,
          ).replace(/\D/g, ""),
          dateOfBirth: String(person.date_of_birth ?? person.dateOfBirth ?? "").trim(),
          phone: String(person.phone ?? "").replace(/\D/g, ""),
          qualified: Boolean(person.qualified),
          phoneQualified: Boolean(person.phone_qualified ?? person.phoneQualified),
          hasPassword: Boolean(person.has_password ?? person.hasPassword),
        }
      : null,
  };
};

/**
 * Creates person (if needed) + active wallet membership.
 * First-access password remains a separate funnel (user-access e-mail).
 */
export const createCarteiraUser = async (payload) => {
  const phoneCountry = String(payload?.phoneCountryDialCode ?? "").replace(/\D/g, "");
  const phoneArea = String(payload?.phoneAreaCode ?? "").replace(/\D/g, "");
  const phoneLocal = String(payload?.phoneLocalNumber ?? "").replace(/\D/g, "");
  const phoneDigits =
    String(payload?.phone ?? "").replace(/\D/g, "") ||
    (phoneCountry && phoneArea && phoneLocal ? `${phoneCountry}${phoneArea}${phoneLocal}` : "");
  const { data } = await api.post("/v1/ib/account-users", {
    email: payload?.email,
    name: payload?.name,
    middle_name: payload?.middleName,
    last_name: payload?.lastName,
    national_registration: payload?.nationalRegistration,
    phone: phoneDigits || undefined,
    phone_country_dial_code: phoneCountry || undefined,
    phone_area_code: phoneArea || undefined,
    phone_local_number: phoneLocal || undefined,
    date_of_birth: payload?.dateOfBirth,
    role: payload?.role,
    permissions: payload?.permissions,
    status: payload?.status ?? "active",
    password_combination: payload?.passwordCombination,
  });

  // New API wraps { user, person_created, invite_kind, verification }; keep backward compat.
  if (data?.user) {
    return {
      user: mapUser(data.user),
      personCreated: Boolean(data.person_created ?? data.personCreated),
      inviteKind: String(data.invite_kind ?? data.inviteKind ?? "").trim() || "first_access",
      accessStatus: String(data.access_status ?? data.accessStatus ?? "pending"),
      emailSent: Boolean(data.email_sent ?? data.emailSent),
      verification: mapVerification(data.verification),
    };
  }

  return {
    user: mapUser(data),
    personCreated: false,
    inviteKind: "first_access",
    accessStatus: "pending",
    emailSent: false,
    verification: null,
  };
};

const mapAccessEmailResend = (data) => ({
  status: String(data?.status ?? "sent"),
  email: String(data?.email ?? "").trim(),
  detail: String(data?.detail ?? "").trim(),
  personId: Number(data?.person_id ?? data?.personId) || null,
  userId: Number(data?.user_id ?? data?.userId) || null,
  inviteKind: String(data?.invite_kind ?? data?.inviteKind ?? "").trim() || "first_access",
  accessStatus: String(data?.access_status ?? data?.accessStatus ?? "pending"),
});

/**
 * Re-send user-access / wallet-grant invite (same SendGrid template, kind from access status).
 */
export const resendCarteiraUserAccessEmail = async (userId) => {
  const { data } = await api.post(`/v1/ib/account-users/${userId}/access-email/resend`);
  return mapAccessEmailResend(data);
};

export const resendCarteiraUserAccessEmailByPerson = async (personId) => {
  const { data } = await api.post(
    `/v1/ib/account-users/person/${personId}/access-email/resend`,
  );
  return mapAccessEmailResend(data);
};

export const resendCarteiraUserAccess = async (user) => {
  const walletId = Number(user?.id);
  if (Number.isFinite(walletId) && walletId > 0) {
    return resendCarteiraUserAccessEmail(walletId);
  }
  const personId = Number(user?.personId);
  if (Number.isFinite(personId) && personId > 0) {
    return resendCarteiraUserAccessEmailByPerson(personId);
  }
  throw new Error("Usuário sem identificador para reenvio de acesso.");
};

export const verifyCarteiraUserInvite = async (userId, payload) => {
  const { data } = await api.post(`/v1/ib/account-users/${userId}/verify`, {
    challenge_id: payload?.challengeId,
    code: payload?.code,
    channel: payload?.channel,
  });
  return mapUser(data);
};

export const resendCarteiraUserVerification = async (userId, payload = {}) => {
  const { data } = await api.post(`/v1/ib/account-users/${userId}/resend-verification`, {
    channel: payload?.channel,
  });
  return mapVerification(data);
};

export const updateCarteiraUser = async (userId, payload) => {
  const { data } = await api.patch(`/v1/ib/account-users/${userId}`, {
    role: payload?.role,
    permissions: payload?.permissions,
    status: payload?.status,
  });
  return mapUser(data);
};

/**
 * Edit role/permissions for AccountPersonAccounts-linked users (wallet id may be 0).
 * Upserts AccountWalletUsers on the server when missing.
 */
export const updateCarteiraUserByPerson = async (personId, payload) => {
  const { data } = await api.patch(`/v1/ib/account-users/person/${personId}`, {
    role: payload?.role,
    permissions: payload?.permissions,
    status: payload?.status,
  });
  return mapUser(data);
};

/**
 * Prefer wallet user id; fall back to person-based update when id is missing/0.
 */
export const saveCarteiraUser = async (user, payload) => {
  const walletId = Number(user?.id);
  if (Number.isFinite(walletId) && walletId > 0) {
    return updateCarteiraUser(walletId, payload);
  }
  const personId = Number(user?.personId);
  if (Number.isFinite(personId) && personId > 0) {
    return updateCarteiraUserByPerson(personId, payload);
  }
  throw new Error("Usuário sem identificador para edição.");
};

export const removeCarteiraUser = async (userId) => {
  await api.delete(`/v1/ib/account-users/${userId}`);
};

export const removeCarteiraUserByPerson = async (personId) => {
  await api.delete(`/v1/ib/account-users/person/${personId}`);
};

export const revokeCarteiraUser = async (user) => {
  const walletId = Number(user?.id);
  if (Number.isFinite(walletId) && walletId > 0) {
    await removeCarteiraUser(walletId);
    return;
  }
  const personId = Number(user?.personId);
  if (Number.isFinite(personId) && personId > 0) {
    await removeCarteiraUserByPerson(personId);
    return;
  }
  throw new Error("Usuário sem identificador para remoção.");
};
